import React, { useRef, useEffect, useState } from "react";
import { ProcessedTripItinerary, TourOperator } from "../contentfulModel.ts";
import { ItineraryMap } from "../ItineraryMap.tsx";
import SVGComponent from "../SVGComponent.tsx";

interface ItineraryDaysPDFProps {
    tourPackage: ProcessedTripItinerary;
    tourOperator: TourOperator;
}

interface DayData {
    fields: {
        day: number;
        title: string;
        description: string;
    }
}


export const ItineraryDaysPDF: React.FC<ItineraryDaysPDFProps> = ({
                                                                      tourPackage,
                                                                      tourOperator
                                                                  }) => {
    // Get days from the tour package
    const days = tourPackage.itinerary || [];
    const [firstPageDays, setFirstPageDays] = useState<DayData[]>([]);
    const [secondPageDays, setSecondPageDays] = useState<DayData[]>([]);
    const [thirdPageDays, setThirdPageDays] = useState<DayData[]>([]);
    const [fourPageDays, setFourPageDays] = useState<DayData[]>([]);

    // Reference to the container to measure height
    const daysContainerRef = useRef<HTMLDivElement>(null);

    // A3 landscape has 29.71cm height
    const maxHeightCm = 20; // Available content height after accounting for headers/padding
    const maxHeightPx = maxHeightCm * (96 / 2.54); // Convert to pixels

    useEffect(() => {
        if (daysContainerRef.current && days.length > 0) {
            setFirstPageDays(days.filter((day) => {
                return !day.group
            }))
            setSecondPageDays(days.filter((day) => {
                return day.group && day.group === 1
            }));
            setThirdPageDays(days.filter((day) => {
                return day.group && day.group === 2
            }));
            setFourPageDays(days.filter((day) => {
                return day.group && day.group === 3
            }));


        }
    }, [days]);

    const renderDay = (day: DayData, index: number) => (
        <div key={index} className="w-1/2 flex flex-col px-2">
            <div className="flex gap-4 justify-start items-start">
                <span className="py-2 font-bold text-start" style={{
                    color: tourOperator.primaryColor || "#000000",
                    fontSize: "12pt",
                    lineHeight: "14pt"
                }}>
                    Día {day.day}  &nbsp; &nbsp;|&nbsp; &nbsp; <span
                    className="py-2 font-light" style={{
                    color: tourOperator.primaryColor || "#000000"
                }}>
                    {day.title}
                </span>
                </span>
            </div>
            <div>
                <p className="text-xs font-light mb-6 text-[#090909] text-start" style={{
                    fontSize: "10pt",
                    lineHeight: "12pt",
                }}>{day.description}</p>
            </div>
        </div>
    );

    const renderPage = (pageDays: DayData[], isFirstPage: boolean) => (
        <div id={`Itinerary-${isFirstPage ? 'First' : 'Second'}`} className="flex items-center justify-center" style={{
            aspectRatio: 1.4136,
            height: "29.71cm",
            width: "42cm",
            pageBreakAfter: isFirstPage && (secondPageDays.length > 0 || thirdPageDays.length > 0 || fourPageDays.length > 0) ? "always" : "auto",
            margin: "0"
        }}>
            <div className="w-[50%] h-full relative">
                {/* Background with filter */}
                <div className="absolute inset-0 h-full" style={{
                    backgroundImage: `url(${tourPackage?.termsAndPricing?.includesImage?.file?.includes(" ") ? encodeURI(tourPackage?.termsAndPricing?.includesImage?.file) : tourPackage?.termsAndPricing?.includesImage?.file})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    filter: "brightness(0.4) contrast(1.2)",
                    zIndex: 0
                }}></div>

                {/* Content without filter */}
                <div className="relative z-10 text-white p-10 h-full flex flex-col">
                    <div className="text-lg font-light mb-4 text-start">{tourPackage.name}</div>
                    <div className="h-full w-full">
                        {tourPackage.countryCodes && !tourPackage.mapImage?.file ?
                        <ItineraryMap tourOperator={tourOperator} itineraryDays={tourPackage.itinerary}
                                                                 countryCodes={tourPackage.countryCodes.split(',')}
                    />  :   (
                            <div className="w-full h-full pt-16 flex items-center justify-center">
                                {tourPackage.mapImage?.file ? (
                                    <SVGComponent
                                        svgUrl={tourPackage.mapImage.file}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <span className="text-gray-500"></span>
                                )}
                </div>
                )}
                    </div>
                </div>
            </div>
            <div className="w-[50%] h-full p-10">
                <div className="flex justify-end">
                    {/* Fixed: Added optional chaining to prevent null reference errors */}
                    <div className="uppercase text-lg font-light text-start"
                    style={{color: tourOperator.primaryColor}}>{tourOperator.name}</div>
                </div>
                <div className="h-full w-full mt-8" ref={isFirstPage ? daysContainerRef : null}>
                    {/* Two columns grid layout - fills vertically first */}
                    <div className="w-full flex flex-wrap h-full flex-col" style={{
                    }}>
                        {pageDays.map((day, index) => renderDay(day, index))}
                    </div>
                </div>
            </div>
        </div>
    );

    // Render both pages if there are more days than can fit on one page
    return (
        <>
            {renderPage([...firstPageDays], true)}
            {secondPageDays.length > 0 && renderPage(secondPageDays, false)}
            {thirdPageDays.length > 0 && renderPage(thirdPageDays, false)}
            {fourPageDays.length > 0 && renderPage(fourPageDays, false)}
        </>
    );
};
